// PlaceDetail — vista de un lugar específico (lleno de historia + acciones)
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Clock, Phone, ExternalLink, Navigation, Camera, Check, Ticket as TicketIcon, X } from "lucide-react";
import { motion } from "framer-motion";
import { TRIP as STATIC_TRIP } from "../data/trip";
import { findPlaceById, resolveStop } from "../data/helpers";
import MapView from "../components/MapView";
import { useGeolocation, distanceMeters, formatDistance, walkingMinutes } from "../hooks/useGeolocation";
import { useTickets } from "../hooks/useTickets";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";
import FavoriteButton from "../components/FavoriteButton";
import TicketViewer from "../components/TicketViewer";
import { useState } from "react";

// Devuelve siempre un array de {src, label} desde la forma legacy o nueva del ticket
function ticketFiles(t) {
  if (!t) return [];
  if (Array.isArray(t.files) && t.files.length) {
    return t.files.map((f, i) => ({
      src: f.data || f.data_b64 || f.src,
      label: f.label || `Archivo ${i + 1}`,
    }));
  }
  const legacy = t.pdf_b64 || t.image_b64;
  return legacy ? [{ src: legacy, label: "Ticket" }] : [];
}

export default function PlaceDetail() {
  const base = useTripBase();
  const { placeId } = useParams();
  const [searchParams] = useSearchParams();
  const dayId = searchParams.get("day");
  const stopIdx = parseInt(searchParams.get("stop") || "-1", 10);
  const navigate = useNavigate();
  const { trip: ctxTrip } = useTrip();
  const T = ctxTrip && ctxTrip.places ? ctxTrip : STATIC_TRIP;
  const place = findPlaceById(placeId, T);
  const ticketInfo = (T.tickets || {})[placeId] || STATIC_TRIP.tickets[placeId];
  const { position } = useGeolocation();
  const { hasTicket, markPurchased, remove, tickets } = useTickets();
  const [showTicketModal, setShowTicketModal] = useState(false);

  // Encontrar día y siguientes/anterior paradas de tipo "place"
  const day = dayId ? T.days.find((d) => d.id === dayId) : null;
  const dayStops = day ? (day.stops || []).map((s) => resolveStop(s, T)).filter(Boolean) : [];
  const placeStops = dayStops.map((s, i) => ({ ...s, _i: i })).filter((s) => s.kind === "place");
  const myIndexInDay = stopIdx >= 0 ? stopIdx : dayStops.findIndex((s) => s.id === placeId);
  const myPlaceOrder = placeStops.findIndex((s) => s._i === myIndexInDay);

  // Buscar la próxima parada de tipo place
  let nextStop = null;
  let prevStop = null;
  if (day && myIndexInDay >= 0) {
    for (let i = myIndexInDay + 1; i < dayStops.length; i++) {
      if (dayStops[i].kind === "place") { nextStop = { stop: dayStops[i], idx: i }; break; }
    }
    for (let i = myIndexInDay - 1; i >= 0; i--) {
      if (dayStops[i].kind === "place") { prevStop = { stop: dayStops[i], idx: i }; break; }
    }
  }

  if (!place) {
    return (
      <div className="container-app pt-10 pb-28">
        <p>Lugar no encontrado.</p>
        <Link to={base || "/"} className="btn-ghost mt-4 inline-block">Volver</Link>
      </div>
    );
  }

  const dist = position ? distanceMeters(position, { lat: place.lat, lng: place.lng }) : null;
  const purchased = hasTicket(placeId);
  const myTicket = tickets[placeId];

  // Distancia y minutos hasta la próxima parada
  const nextPlaceData = nextStop?.stop?.data;
  const distToNext = nextPlaceData ? distanceMeters({ lat: place.lat, lng: place.lng }, { lat: nextPlaceData.lat, lng: nextPlaceData.lng }) : null;

  // Open the place in Google Maps. Prefer the textual address (so the pin
  // matches what Google Maps shows when you search the address — this avoids
  // misaligned lat/lng misplacing the marker by tens of meters), and fall
  // back to coordinates when no address is available.
  const openInMaps = (target) => {
    if (!target) return;
    const destination = target.address
      ? encodeURIComponent(target.address)
      : `${target.lat},${target.lng}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=walking`;
    window.open(url, "_blank");
  };

  return (
    <div className="pb-44" data-testid={`place-${placeId}`}>
      {/* Hero */}
      {place.hero ? (
        <div className="hero-img relative h-56">
          <img src={place.hero} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          <button onClick={() => day ? navigate(`${base}/dia/${day.id}`) : navigate(-1)} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md active:scale-95" data-testid="place-back">
            <ChevronLeft size={20} />
          </button>
          {day && myPlaceOrder >= 0 && (
            <Link to={`${base}/dia/${day.id}`} className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold text-[hsl(var(--ink))] shadow-md" data-testid="place-stop-badge">
              Parada {myPlaceOrder + 1} de {placeStops.length}
            </Link>
          )}
          <div className="absolute top-4 right-4 z-10" style={{ right: day && myPlaceOrder >= 0 ? 120 : 16 }}>
            <div className="bg-white/90 backdrop-blur rounded-full">
              <FavoriteButton kind="place" ref={place.id} size={16} />
            </div>
          </div>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{place.icon}</span>
              {place.jewish && <span className="chip-jewish text-[10px]">✡ Judío</span>}
              {place.noPork && <span className="chip-gold text-[10px]">Sin cerdo ✓</span>}
            </div>
            <h1 className="text-2xl font-bold leading-tight">{place.name}</h1>
            {place.nameLocal && <p className="text-sm opacity-90 italic">{place.nameLocal}</p>}
            {place.address && (
              <p className="text-xs opacity-95 mt-1.5 flex items-start gap-1">
                <MapPin size={11} className="shrink-0 mt-0.5" />
                <span>{place.address}</span>
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="container-app pt-6">
          <button onClick={() => day ? navigate(`${base}/dia/${day.id}`) : navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-3" data-testid="place-back">
            <ChevronLeft size={16} /> {day ? `Día ${day.dayLabel.toLowerCase()}` : "Atrás"}
          </button>
          {day && myPlaceOrder >= 0 && (
            <div className="mb-2">
              <span className="chip-ink">Parada {myPlaceOrder + 1} de {placeStops.length}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{place.icon}</span>
            {place.jewish && <span className="chip-jewish">✡ Judío</span>}
            {place.noPork && <span className="chip-gold">Sin cerdo ✓</span>}
          </div>
          <h1 className="text-3xl font-bold leading-tight">{place.name}</h1>
          {place.nameLocal && <p className="text-sm text-[hsl(var(--muted))] italic mt-1">{place.nameLocal}</p>}
          {place.address && (
            <p className="text-sm text-[hsl(var(--ink-soft))] mt-2 flex items-start gap-1.5" data-testid="place-address">
              <MapPin size={13} className="shrink-0 mt-0.5 text-[hsl(var(--accent))]" />
              <span>{place.address}</span>
            </p>
          )}
        </div>
      )}

      <div className="container-app mt-6 space-y-5">
        {/* Acciones */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => openInMaps(place)} className="btn-primary flex items-center justify-center gap-2" data-testid="place-directions">
              <Navigation size={16} /> Cómo llegar aquí
            </button>
            {ticketInfo && ticketInfo.status === "advance" && (
              <button onClick={() => setShowTicketModal(true)} className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-medium text-sm transition-all active:scale-95 ${purchased ? "bg-[hsl(140_50%_92%)] text-[hsl(140_60%_28%)]" : "bg-[hsl(0_80%_95%)] text-[hsl(0_70%_40%)]"}`} data-testid="place-ticket-btn">
                {purchased ? <><Check size={16} /> Ticket OK</> : <><TicketIcon size={16} /> Reservar</>}
              </button>
            )}
            {ticketInfo && ticketInfo.status === "onsite" && (
              <button onClick={() => setShowTicketModal(true)} className="btn-ghost flex items-center justify-center gap-2" data-testid="place-ticket-btn">
                <TicketIcon size={16} /> {purchased ? "Marcado ✓" : "Marcar comprado"}
              </button>
            )}
          </div>
          {nextPlaceData && (
            <button onClick={() => openInMaps(nextPlaceData)} className="btn-ghost w-full flex items-center justify-center gap-2 text-xs" data-testid="place-directions-next">
              <Navigation size={14} /> Cómo llegar a la próxima parada · {nextPlaceData.name}
            </button>
          )}
          <Link
            to={`${base}/ar?target=${place.id}`}
            className="btn-ghost w-full flex items-center justify-center gap-2 text-xs"
            data-testid="place-ar-navigate"
          >
            <Camera size={14} /> Guiame con AR (cámara + flecha)
          </Link>
        </div>

        {/* GPS distancia */}
        {dist != null && (
          <div className="card-paper p-3 flex items-center justify-between" data-testid="place-distance">
            <div className="flex items-center gap-2">
              <Navigation size={16} className="text-[hsl(var(--accent))]" />
              <p className="text-sm font-medium">Desde donde estás</p>
            </div>
            <div className="text-right">
              <p className="text-base font-bold text-[hsl(var(--accent))]">{formatDistance(dist)}</p>
              <p className="text-[11px] text-[hsl(var(--muted))]">≈ {walkingMinutes(dist)} min a pie</p>
            </div>
          </div>
        )}

        {/* Ticket info */}
        {ticketInfo && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-paper p-4">
            <div className="flex items-start gap-3">
              <TicketIcon size={18} className="shrink-0 mt-0.5 text-[hsl(var(--accent))]" />
              <div className="flex-1">
                <p className="font-semibold text-sm">
                  {ticketInfo.status === "advance" && "Reserva anticipada recomendada"}
                  {ticketInfo.status === "onsite" && "Entrada se compra en la puerta"}
                  {ticketInfo.status === "free" && "Entrada gratis"}
                  {ticketInfo.status === "free+paid" && "Mixto: hay zona gratis y zona de pago"}
                </p>
                {ticketInfo.price && <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">{ticketInfo.price}</p>}
                {ticketInfo.note && <p className="text-xs text-[hsl(var(--muted))] mt-2 leading-relaxed">{ticketInfo.note}</p>}
                {ticketInfo.url && (
                  <a href={ticketInfo.url.startsWith("http") ? ticketInfo.url : `https://${ticketInfo.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[hsl(var(--accent))] font-medium text-sm mt-2" data-testid="place-ticket-url">
                    {ticketInfo.url} <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
            {purchased && myTicket && (
              <div className="mt-3 pt-3 border-t border-[hsl(var(--line))]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[hsl(140_60%_28%)]">
                    <Check size={14} />
                    <span className="text-xs font-medium">Ticket guardado</span>
                  </div>
                  <button onClick={() => remove(placeId)} className="text-xs text-[hsl(var(--muted))] underline" data-testid="place-ticket-remove">Quitar</button>
                </div>
                {myTicket.note && <p className="text-xs text-[hsl(var(--ink-soft))] mt-2">📝 {myTicket.note}</p>}
                <TicketViewer
                  files={ticketFiles(myTicket)}
                  alt={`Ticket ${place.name}`}
                />
              </div>
            )}
          </motion.section>
        )}

        {/* Historia */}
        {place.story && (
          <section className="card-paper p-5">
            <h3 className="section-title mb-3">La historia</h3>
            <p className="serif text-[17px] leading-[1.65] text-[hsl(var(--ink))]">{place.story}</p>
          </section>
        )}

        {/* Datos prácticos */}
        <section className="card-paper p-5 space-y-3">
          {place.hours && (
            <div className="flex gap-3">
              <Clock size={16} className="shrink-0 mt-0.5 text-[hsl(var(--muted))]" />
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Horarios</p>
                <p className="text-sm">{place.hours}</p>
              </div>
            </div>
          )}
          {place.reserve && (
            <div className="flex gap-3">
              <Phone size={16} className="shrink-0 mt-0.5 text-[hsl(var(--muted))]" />
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Reservar</p>
                <p className="text-sm">{place.reserve}</p>
              </div>
            </div>
          )}
          {place.cuisine && (
            <div className="flex gap-3">
              <span className="shrink-0 mt-0.5">🍽️</span>
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Cocina</p>
                <p className="text-sm">{place.cuisine}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <MapPin size={16} className="shrink-0 mt-0.5 text-[hsl(var(--muted))]" />
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Ubicación</p>
              <p className="text-sm">{place.city}</p>
              <p className="text-xs text-[hsl(var(--muted))]">{place.lat.toFixed(4)}, {place.lng.toFixed(4)}</p>
            </div>
          </div>
        </section>

        {/* Carta */}
        {place.menu && place.menu.length > 0 && (
          <section className="card-paper p-5">
            <h3 className="section-title mb-3">Carta · qué pedir</h3>
            <ul className="space-y-1.5">
              {place.menu.map((m, i) => (
                <li key={i} className="text-sm text-[hsl(var(--ink-soft))] flex gap-2">
                  <span className="text-[hsl(var(--accent))] shrink-0">·</span>
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Tip */}
        {place.tip && (
          <section className="card-paper p-4" style={{ background: "hsl(var(--accent-soft))" }}>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))] mb-1">💡 Tip</p>
            <p className="text-sm text-[hsl(var(--ink))]">{place.tip}</p>
          </section>
        )}

        {/* No pork note */}
        {place.noPorkNote && (
          <section className="card-paper p-4" style={{ background: "hsl(var(--jewish-soft))" }}>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--jewish))] mb-1">🥩 Sobre el cerdo</p>
            <p className="text-sm text-[hsl(var(--ink))]">{place.noPorkNote}</p>
          </section>
        )}

        {/* Mini mapa con ruta a próxima parada si existe */}
        <div className="card-paper overflow-hidden" style={{ height: 240 }}>
          <MapView
            center={[place.lat, place.lng]}
            zoom={15}
            markers={[
              { id: place.id, lat: place.lat, lng: place.lng, emoji: place.icon, name: place.name, kind: place.jewish ? "jewish" : place.type === "food" ? "food" : "" },
              ...(nextPlaceData ? [{ id: nextStop.stop.id, lat: nextPlaceData.lat, lng: nextPlaceData.lng, emoji: nextPlaceData.icon || "📍", name: `→ ${nextPlaceData.name}`, kind: nextPlaceData.jewish ? "jewish" : nextPlaceData.type === "food" ? "food" : "" }] : []),
            ]}
            userPosition={position}
            routeToActive={
              nextPlaceData
                ? { from: { lat: place.lat, lng: place.lng }, to: { lat: nextPlaceData.lat, lng: nextPlaceData.lng } }
                : position
                  ? { from: position, to: { lat: place.lat, lng: place.lng } }
                  : null
            }
            fitBounds={!!nextPlaceData}
            height="100%"
          />
        </div>

        {/* Navegación entre paradas del día */}
        {day && (
          <section className="card-paper p-4 mt-1" data-testid="place-day-nav">
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-3">
              Día {day.dayLabel.toLowerCase()} · {day.city}
            </p>
            <div className="flex items-center gap-2">
              {prevStop ? (
                <Link
                  to={`${base}/lugar/${prevStop.stop.id}?day=${day.id}&stop=${prevStop.idx}`}
                  className="btn-ghost flex-1 flex items-center justify-center gap-1.5 text-xs"
                  data-testid="place-prev-stop"
                >
                  <ChevronLeft size={14} /> Parada anterior
                </Link>
              ) : <div className="flex-1" />}
              <Link to={`${base}/dia/${day.id}`} className="btn-ghost flex-1 flex items-center justify-center text-xs" data-testid="place-back-to-day">
                Ver día completo
              </Link>
            </div>
          </section>
        )}
      </div>

      {/* Barra flotante INFERIOR: Llegar aquí + Continuar.
          Bottom nav fija usa env(safe-area-inset-bottom) + ~64px de iconos.
          Dejamos ~100px de espacio para que la barra flotante quede claramente
          arriba de la tab nav y no la tape. */}
      <div
        className="fixed left-0 right-0 z-30 px-3"
        style={{
          bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
          paddingLeft: "calc(12px + env(safe-area-inset-left, 0px))",
          paddingRight: "calc(12px + env(safe-area-inset-right, 0px))",
        }}
      >
        <div className="max-w-xl mx-auto flex gap-2">
          {/* Llegar aquí — usa la dirección textual cuando existe */}
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${place.address ? encodeURIComponent(place.address) : `${place.lat},${place.lng}`}&travelmode=walking`}
            target="_blank"
            rel="noreferrer"
            className="flex-1 min-w-0 rounded-full bg-white border border-[hsl(var(--line))] px-3 py-3 shadow-lg active:scale-[0.98] transition-transform"
            data-testid="place-go-here-fab"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shrink-0">
                <Navigation size={14} />
              </div>
              <div className="text-left min-w-0 flex-1">
                <p className="text-[9px] uppercase tracking-wider opacity-60 font-semibold leading-none">Llegar aquí</p>
                <p className="text-xs font-bold truncate leading-tight mt-0.5">
                  {dist != null ? `${formatDistance(dist)} · ${walkingMinutes(dist)} min` : "Abrir mapa"}
                </p>
              </div>
            </div>
          </a>

          {/* Continuar al próximo */}
          {nextStop && nextPlaceData && (
            <Link
              to={`${base}/lugar/${nextStop.stop.id}?day=${day.id}&stop=${nextStop.idx}`}
              className="flex-1 min-w-0 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-3 py-3 shadow-lg active:scale-[0.98] transition-transform"
              data-testid="place-continue-fab"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="text-left min-w-0 flex-1">
                  <p className="text-[9px] uppercase tracking-wider opacity-70 font-semibold leading-none">Próximo →</p>
                  <p className="text-xs font-bold truncate leading-tight mt-0.5">{nextPlaceData.icon} {nextPlaceData.name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold">{formatDistance(distToNext)}</p>
                  <p className="text-[9px] opacity-70">{walkingMinutes(distToNext)} min</p>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Modal de ticket */}
      {showTicketModal && (
        <TicketModal
          place={place}
          existing={myTicket}
          onClose={() => setShowTicketModal(false)}
          onSave={(payload) => { markPurchased(placeId, payload); setShowTicketModal(false); }}
        />
      )}
    </div>
  );
}

function TicketModal({ place, existing, onClose, onSave }) {
  const [note, setNote] = useState(existing?.note || "");
  // Inicializa con archivos existentes (soporta formato legacy y nuevo)
  const [files, setFiles] = useState(() => {
    if (Array.isArray(existing?.files) && existing.files.length) {
      return existing.files.map((f) => ({ data: f.data || f.data_b64 || f.src, type: (f.data || f.data_b64 || f.src || "").startsWith("data:application/pdf") ? "pdf" : "image", label: f.label || "" }));
    }
    if (existing?.pdf_b64) return [{ data: existing.pdf_b64, type: "pdf", label: "" }];
    if (existing?.image_b64) return [{ data: existing.image_b64, type: "image", label: "" }];
    return [];
  });

  const onFileInput = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    list.forEach((f) => {
      const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      const reader = new FileReader();
      reader.onload = () => {
        setFiles((prev) => [...prev, { data: reader.result, type: isPdf ? "pdf" : "image", label: f.name.replace(/\.[^.]+$/, "") }]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const removeAt = (i) => setFiles((prev) => prev.filter((_, k) => k !== i));
  const setLabel = (i, label) => setFiles((prev) => prev.map((f, k) => (k === i ? { ...f, label } : f)));

  const handleSave = () => {
    const payload = {
      note,
      files: files.map((f) => ({ data: f.data, label: f.label || "" })),
      // Backward-compat: si hay al menos uno, también seteamos el campo legacy con el primero
      image_b64: files.find((f) => f.type === "image")?.data || null,
      pdf_b64: files.find((f) => f.type === "pdf")?.data || null,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="ticket-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-1">Guardar ticket</h3>
        <p className="text-xs text-[hsl(var(--muted))] mb-4">{place.name}</p>

        <label className="block text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted))] mb-1">
          Fotos / PDFs del ticket {files.length > 0 && <span className="normal-case text-[hsl(var(--accent))]">({files.length})</span>}
        </label>

        {/* Lista de archivos cargados */}
        {files.length > 0 && (
          <div className="space-y-2 mb-3" data-testid="ticket-modal-files">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-xl border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))]">
                {f.type === "image" ? (
                  <img src={f.data} alt="" className="w-12 h-12 object-cover rounded border" />
                ) : (
                  <div className="w-12 h-12 rounded border bg-white flex items-center justify-center">
                    <Camera size={18} className="text-[hsl(var(--accent))]" />
                  </div>
                )}
                <input
                  type="text"
                  value={f.label}
                  onChange={(e) => setLabel(i, e.target.value)}
                  placeholder={`Etiqueta (ej. "Alan", "Carolina")`}
                  className="flex-1 min-w-0 px-2 py-1 text-sm rounded border border-[hsl(var(--line))] bg-white"
                  data-testid={`ticket-file-label-${i}`}
                />
                <button onClick={() => removeAt(i)} className="text-[hsl(var(--muted))] p-1 active:scale-90" aria-label="Quitar" data-testid={`ticket-file-remove-${i}`}>
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="block card-paper p-4 mb-4 text-center cursor-pointer" data-testid="ticket-upload">
          <Camera size={20} className="mx-auto mb-1 text-[hsl(var(--accent))]" />
          <span className="text-sm">{files.length > 0 ? `Agregar otra foto o PDF` : "Subir foto o PDF · podés agregar varias"}</span>
          <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onFileInput} />
        </label>

        <label className="block text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted))] mb-1">Nota (opcional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Ej: confirmation #ABC123, horario 10:00, fila A asientos 5-6..."
          className="w-full p-3 rounded-xl border border-[hsl(var(--line))] text-sm bg-white"
          data-testid="ticket-note"
        />

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost flex-1" data-testid="ticket-cancel">Cancelar</button>
          <button onClick={handleSave} className="btn-primary flex-1" data-testid="ticket-save">
            Guardar ticket
          </button>
        </div>
      </div>
    </div>
  );
}
